<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Card padding-class="tw-p-5" class="tw-relative tw-overflow-hidden">
      <div
        v-if="isBlurred"
        class="tw-absolute tw-inset-0 tw-z-50 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-backdrop-blur"
      >
        <BaseButton
          color="primary"
          @click="openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)"
        >
          <LockIcon class="tw-mr-2 tw-h-4 tw-w-4" />
          Create Account to Unlock All Mentors
        </BaseButton>
      </div>
      <div
        ref="cardWrapperRef"
        :class="{ 'tw-pointer-events-none tw-select-none': isBlurred }"
      >
        <div class="tw-flex tw-flex-wrap md:tw-flex-nowrap">
          <div class="tw-order-1 tw-shrink-0 tw-grow md:tw-mr-4 md:tw-grow-0">
            <SkeletonLoader
              :is-loading="isLoading"
              size-classes="tw-size-[144px] tw-rounded-xl"
            >
              <a :href="mentor.personalUrl" target="_blank">
                <Avatar
                  size="3xl"
                  :src="mentor.avatarUrl"
                  :alt="`${mentor.displayName}'s avatar`"
                  :is-featured="mentor.featured"
                  square
                />
              </a>
            </SkeletonLoader>
          </div>
          <div
            class="tw-order-3 tw-mt-4 tw-flex tw-grow tw-flex-col tw-space-y-2 tw-self-center md:tw-order-2 md:tw-mt-0"
          >
            <div
              class="tw-flex tw-flex-col tw-space-y-1 lg:tw-flex-row lg:tw-items-center lg:tw-space-x-3 lg:tw-space-y-0"
            >
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-44 tw-h-8 tw-rounded-full"
              >
                <a
                  class="tw-order-2 tw-mt-2 tw-w-max hover:tw-text-neutral-700 md:tw-order-1 md:tw-mt-0"
                  :href="mentor.personalUrl"
                  target="_blank"
                >
                  <h2 class="tw-text-2xl tw-font-bold">
                    {{ mentor.displayName }}
                  </h2>
                </a>
              </SkeletonLoader>
              <AddMentorToListButton
                v-if="!isLoading"
                :mentor-id="mentor.id"
                :mentor-added-to-lists="mentorAddedToLists"
                class="tw-order-1 md:tw-order-2"
              >
                <template
                  #button="{
                    openModal: openListModal,
                    isAddedToAnyList,
                    lists,
                  }"
                >
                  <BaseButton
                    size="xs"
                    :color="
                      isAddedToAnyList ? 'secondary' : 'outline-secondary'
                    "
                    @click="openListModal"
                    :is-disabled="isMentorCurrentUser"
                  >
                    <template v-if="!isAddedToAnyList">
                      <AddToListIcon
                        class="tw-ml-0.5 tw-mr-2 tw-h-3 tw-w-3 tw-shrink-0"
                      />
                      Add to list
                    </template>
                    <template v-else>
                      <CheckIcon class="tw-mr-2 tw-h-3 tw-w-3 tw-shrink-0" />
                      Added to
                      {{ lists.map((name) => `"${name}"`).join(', ') }}
                      {{ lists.length > 1 ? 'lists' : 'list' }}
                    </template>
                  </BaseButton>
                </template>
              </AddMentorToListButton>
            </div>
            <SkeletonLoader
              :is-loading="isLoading"
              size-classes="tw-w-52 tw-h-4 tw-rounded-full"
            >
              <div class="tw-text-neutral-600">
                {{ mentor.professionTitle }}
              </div>
            </SkeletonLoader>
            <div class="tw-text-xs tw-text-neutral-400">
              <div class="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2">
                <span class="tw-inline-flex tw-items-center tw-gap-1.5">
                  <SkeletonLoader
                    :is-loading="isLoading"
                    size-classes="tw-w-64 tw-h-2 tw-rounded-full tw-mt-1"
                  >
                    <LocationIcon class="tw--mt-0.5 tw-h-[15px] tw-w-[11px]" />
                    <span>{{ mentor.livingInWithTimezone }}</span>
                  </SkeletonLoader>
                </span>
                <span
                  v-if="mentor.languageList && mentor.languageList.length"
                  class="tw-inline-flex tw-items-center tw-gap-1.5"
                >
                  <SkeletonLoader
                    :is-loading="isLoading"
                    size-classes="tw-w-24 tw-h-2 tw-rounded-full tw-mt-1"
                  >
                    <GlobeIcon class="tw-h-4 tw-w-4" />
                    <span>{{ mentor.languageList.join(', ') }}</span>
                  </SkeletonLoader>
                </span>
                <span>
                  <SkeletonLoader
                    :is-loading="isLoading"
                    size-classes="tw-w-16 tw-h-2 tw-rounded-full tw-mt-1"
                  >
                    {{ mentor.fromDetails }}
                  </SkeletonLoader>
                </span>
                <div
                  v-if="mentor.respondsIn"
                  class="tw-inline-flex tw-items-center tw-gap-1.5"
                >
                  <SkeletonLoader
                    :is-loading="isLoading"
                    size-classes="tw-w-32 tw-h-2 tw-rounded-full tw-mt-1"
                  >
                    <LightningIcon
                      class="tw-h-3.5 tw-w-3.5 tw-text-success-500"
                    />
                    Usually responds in {{ mentor.respondsIn }}
                  </SkeletonLoader>
                </div>
              </div>
            </div>
          </div>
          <div
            class="tw-order-2 tw-flex tw-shrink-0 tw-flex-col tw-items-end md:tw-order-3 md:tw-w-[240px]"
          >
            <div
              class="tw-flex tw-items-center tw-space-x-2 tw-text-2xl tw-font-semibold"
            >
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-24 tw-h-8 tw-rounded-lg"
              >
                <div class="tw-shrink-0">
                  <StarIcon class="tw-h-4 tw-w-4 tw-text-warning-500" />
                </div>
                <span>{{ parseFloat(mentor.rating).toFixed(2) }}</span>
              </SkeletonLoader>
            </div>
            <SkeletonLoader
              :is-loading="isLoading"
              size-classes="tw-w-32 tw-h-3 tw-rounded-full tw-mt-2"
            >
              <div class="tw-text-xs tw-text-neutral-400">
                {{ mentor.reviewsForMeCount }} reviews /
                {{ mentor.coursesCount }} sessions
              </div>
            </SkeletonLoader>
            <div
              class="tw-mt-3 tw-flex tw-items-center tw-space-x-2 tw-font-semibold"
            >
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-20 tw-h-6 tw-rounded-full"
              >
                <span :class="{ 'tw-text-success-500': isCourseFree }">
                  {{ priceRate }}
                </span>
                <CreditCardIcon class="tw-h-4 tw-w-4 tw-text-neutral-300" />
              </SkeletonLoader>
            </div>
          </div>
        </div>
        <hr class="tw-mb-4 tw-mt-2 md:tw-hidden" />
        <div class="tw-mt-4 tw-justify-between md:tw-flex md:tw-space-x-6">
          <div>
            <p class="tw-text-neutral-600">
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-92 tw-h-4 tw-rounded-full"
              >
                {{ mentor.aboutMe }}
              </SkeletonLoader>
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-64 tw-h-4 tw-rounded-full tw-mt-2"
              />
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-72 tw-h-4 tw-rounded-full tw-mt-2"
              />
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-48 tw-h-4 tw-rounded-full tw-mt-2"
              />
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-60 tw-h-4 tw-rounded-full tw-mt-2"
              />
            </p>
            <ExpertiseTabs
              v-if="isExpertiseSectionShown && !isLoading"
              class="tw-mt-4"
            >
              <ExpertiseTab title="Expertise">
                <div class="tw-mt-4 tw-flex tw-flex-wrap tw-gap-3">
                  <MentorExpertiseTag
                    v-for="(specialty, index) in mentor.specialties"
                    :key="index"
                    :name="specialty.name"
                    :description="specialty.description"
                  >
                    {{ specialty.name }}
                  </MentorExpertiseTag>
                </div>
              </ExpertiseTab>
              <ExpertiseTab title="Industry">
                <div class="tw-mt-4 tw-flex tw-flex-wrap tw-gap-3">
                  <MentorExpertiseTag
                    v-for="(industry, index) in mentor.industries"
                    :key="index"
                    :name="industry.name"
                    :description="industry.description"
                  >
                    {{ industry.name }}
                  </MentorExpertiseTag>
                </div>
              </ExpertiseTab>
              <ExpertiseTab title="Tools">
                <div class="tw-mt-4 tw-flex tw-flex-wrap tw-gap-3">
                  <MentorExpertiseTag
                    v-for="(tool, index) in mentor.toolsList"
                    :key="index"
                    :name="tool.name"
                    :description="tool.description"
                  >
                    {{ tool.name }}
                  </MentorExpertiseTag>
                </div>
              </ExpertiseTab>
            </ExpertiseTabs>
          </div>
          <hr class="tw-my-4 md:tw-hidden" />
          <div class="tw-shrink-0 md:tw-w-[240px]">
            <SkeletonLoader
              :is-loading="isLoading"
              size-classes="tw-w-full tw-h-20 tw-rounded-lg"
            >
              <NextAvailable
                :is-available="mentor.available"
                :next-available-time="mentor.nextAvailableTime"
              />
            </SkeletonLoader>
            <div class="tw-mt-4 tw-flex tw-flex-col tw-space-y-2">
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-full tw-h-10 tw-rounded-full"
              >
                <RequestCallButton
                  :is-available="mentor.available"
                  :mentor-slug="mentor.slug"
                  :is-mentor-chargeable="mentor.chargeable"
                  :modal-data="{
                    avatarUrl: mentor.avatarUrl,
                    displayName: mentor.displayName,
                    firstName: mentor.firstName,
                    professionTitle: mentor.professionTitle,
                    livingInWithTimezone: mentor.livingInWithTimezone,
                    languageList: mentor.languageList,
                    fromDetails: mentor.fromDetails,
                    respondsIn: mentor.respondsIn,
                    featuredReview: mentor.featuredReview,
                  }"
                />
              </SkeletonLoader>
              <SkeletonLoader
                :is-loading="isLoading"
                size-classes="tw-w-full tw-h-10 tw-rounded-full"
              >
                <BaseButton
                  color="outline-secondary"
                  is-block
                  :href="mentor.personalUrl"
                  target="_blank"
                >
                  View Profile
                </BaseButton>
              </SkeletonLoader>
            </div>
          </div>
        </div>
      </div>
    </Card>
    <slot name="bottom" />
  </div>
</template>

<script setup>
import Avatar from '@/shared/components/avatar/Avatar.vue'
import Card from '@/shared/components/Card.vue'
import ExpertiseTabs from '@/shared/components/tabs/ExpertiseTabs/ExpertiseTabs.vue'
import ExpertiseTab from '@/shared/components/tabs/ExpertiseTabs/ExpertiseTab.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import RequestCallButton from '@/shared/components/RequestCallButton.vue'
import LocationIcon from '@/assets/images/icons/location.svg'
import GlobeIcon from '@/assets/images/icons/globe.svg'
import StarIcon from '@/assets/images/icons/star.svg'
import CreditCardIcon from '@/assets/images/icons/credit-card.svg'
import LockIcon from '@/assets/images/icons/lock.svg'
import AddToListIcon from '@/assets/images/icons/add-to-list.svg'
import CheckIcon from '@/assets/images/icons/check.svg'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import AddMentorToListButton from '@/shared/components/AddMentorToListButton.vue'
import LightningIcon from '@/assets/images/icons/lightning.svg'
import MentorExpertiseTag from '@/modules/growthmentor/mentors/components/MentorExpertiseTag.vue'
import NextAvailable from '@/modules/growthmentor/mentors/components/NextAvailable.vue'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { useModals } from '@/shared/composables/useModals.js'
import SkeletonLoader from '@/shared/components/SkeletonLoader.vue'

const props = defineProps({
  mentor: {
    type: Object,
    required: true,
  },
  isBlurred: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  mentorAddedToLists: {
    type: Array,
    default: () => [],
  },
  isExpertiseSectionShown: {
    type: Boolean,
    default: false,
  },
})

const { currentUser } = useCurrentUser()
const { openModal } = useModals()

const cardWrapperRef = ref(null)

const isCourseFree = computed(() => {
  return Number(props.mentor.hourRate) === 0
})
const priceRate = computed(() => {
  if (isCourseFree.value) return 'Free'
  return `${props.mentor.hourRateCurrency} / hour`
})
const isMentorCurrentUser = computed(() => {
  return props.mentor.id === currentUser.value.id
})

onMounted(() => {
  // Disable tabbing in the entire card if is blurred
  if (props.isBlurred) {
    // nextTick is needed because ExpertiseTabs are not mounted yet, so we can't loop through them
    nextTick(() => {
      const focusableElements = cardWrapperRef.value.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      focusableElements.forEach((element) => {
        element.setAttribute('tabindex', '-1')
      })
    })
  }
})
</script>
